import { Alert, Button, Col, Form, List, Row, Tag } from "antd";
import { buttonStyle, fullWidthStyle, titleStyle } from "../../constants/style";
import { SubmitStatus } from "../../api/work-order-api";
import ApiContext from "../../api/context";
import { useContext, useEffect, useState } from "react";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { Template } from "../../api/models";
import { toDateString, toDateTimeString, toPrintableTemplateStatus, toTemplateDetailItemTypeColor } from "../../constants";

type TemplateDetailsProps = {
  template?: Template,
  onClose: () => void,
  onCreateWorkOrder: () => void,
  onDeleteRequested: () => void,
  onRenameRequested: () => void
};

type TemplateDetailItem = {
  name: string,
  type: "File" | "File Sequence" | "CSV Parameter" | "Smart Folder Parameter"
};

function toDetailItems(template?: Template): TemplateDetailItem[] {
  const items: TemplateDetailItem[] = [];
  for (const file of template?.files ?? []) {
    items.push({ name: file, type: "File" });
  }

  for (const fileSequence of template?.fileSequences ?? []) {
    items.push({ name: fileSequence.assetPathPattern, type: "File Sequence" });
  }

  const seenCsvAssetPaths = new Set<string>();
  for (const csvParameter of template?.csvParameters ?? []) {
    if (seenCsvAssetPaths.has(csvParameter.assetPath)) {
      continue;
    }

    seenCsvAssetPaths.add(csvParameter.assetPath);
    items.push({ name: csvParameter.assetPath, type: "CSV Parameter" });
  }

  for (const smartFolder of template?.smartFolderParameters ?? []) {
    items.push({ name: smartFolder.assetPath, type: "Smart Folder Parameter" });
  }

  return items;
}

export default function (p: TemplateDetailsProps) {
  const [template, setTemplate] = useState<Template | undefined>();
  const [loadStatus, setLoadStatus] = useState<SubmitStatus>(SubmitStatus.Submitting);
  const [error, setError] = useState<string>();
  const { api } = useContext(ApiContext);
  const isLoading = loadStatus === SubmitStatus.Submitting;
  const detailItems = toDetailItems(template);

  useEffect(() => {
    api?.getTemplate(p.template?.templateID!).then(response => {
      if (!response.good() || !response.body) {
        setError("Failed to get the template details.");
        return;
      }

      setTemplate(response.body);
    }).catch(() => {
      setError("Failed to get the template details.");
    }).finally(() => {
      setLoadStatus(SubmitStatus.Idle);
    });
  }, []);

  return (
    <>
      <Title style={titleStyle} level={4}>{p.template?.name}</Title>
      <Form
        layout="vertical"
        colon={false}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Template ID">
              <Paragraph strong>{p.template?.templateID}</Paragraph>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Status">
              <Paragraph strong>{p.template?.status ? toPrintableTemplateStatus(p.template.status.templateStatusType) : undefined}</Paragraph>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Creation Time">
              <Paragraph strong>{toDateString(p.template?.creationTime)}</Paragraph>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Last Status Time">
              <Paragraph strong>{toDateTimeString(p.template?.status?.statusTime)}</Paragraph>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Files">
          <List
            bordered={false}
            footer={false}
            loading={isLoading}
            pagination={{ pageSize: 5, hideOnSinglePage: detailItems.length < 6 }}
            size="small"
            dataSource={detailItems}
            locale={{ emptyText: isLoading ? "Loading..." : "There are no files associated with this template." }}
            renderItem={(item: TemplateDetailItem) =>
              <List.Item key={item.name}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={fullWidthStyle}>
                  <span>{item.name}</span>
                  <Tag color={toTemplateDetailItemTypeColor(item.type)}>{item.type}</Tag>
                </Row>
              </List.Item>
            }
          >
          </List>
        </Form.Item>
        <Row style={fullWidthStyle} justify="end" align="middle">
          <Button
            type="default"
            style={buttonStyle}
            onClick={p.onClose}>
            Close
          </Button>
          <Button
            type="default"
            style={buttonStyle}
            onClick={p.onRenameRequested}>
            Rename
          </Button>
          <Button
            danger
            type="default"
            style={buttonStyle}
            onClick={p.onDeleteRequested}>
            Delete
          </Button>
          <Button
            type="primary"
            style={buttonStyle}
            onClick={p.onCreateWorkOrder}>
            Create Work Order
          </Button>
        </Row>
        {
          !error ? undefined :
            <>
              <Paragraph></Paragraph>
              <Alert style={fullWidthStyle} type="error" title={error} />
            </>
        }
      </Form>
    </>);
}
